<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { formatReportDuration, durationMetricTooltip } from '../constants';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';

const props = defineProps({
  summary: {
    type: Object,
    default: () => ({}),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { openArchive } = useSummaryReportArchive();

const METRIC_ARCHIVE_KEY = {
  first_human_reply: 'firstResponseCount',
  subsequent_reply: 'replyTimeCount',
  resolution_time: 'resolutionsCount',
};

const rows = computed(() => [
  {
    key: 'first_human_reply',
    label: t('CHATS_REPORTS.TIME_SUMMARY.FIRST_HUMAN_REPLY'),
    ...(props.summary.firstHumanReply || {}),
  },
  {
    key: 'subsequent_reply',
    label: t('CHATS_REPORTS.TIME_SUMMARY.SUBSEQUENT_REPLY'),
    ...(props.summary.subsequentReply || {}),
  },
  {
    key: 'resolution_time',
    label: t('CHATS_REPORTS.TIME_SUMMARY.RESOLUTION_TIME'),
    ...(props.summary.resolutionTime || {}),
  },
]);

const formatSeconds = value =>
  value === null || value === undefined ? '--' : formatReportDuration(value, t);

const openConversation = displayId => {
  if (!displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: displayId,
    },
  });
};

const openValidCycles = row => {
  const metricKey = METRIC_ARCHIVE_KEY[row.key];
  if (!metricKey) return;

  openArchive({
    metricKey,
    reportType: 'account',
    row: null,
    context: props.archiveContext,
  });
};
</script>

<template>
  <section
    class="relative mt-5 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="px-2 py-2 text-base font-medium text-n-slate-12">
      {{ $t('CHATS_REPORTS.TIME_SUMMARY.TITLE') }}
    </h3>
    <div class="overflow-x-auto">
      <table class="w-full table-fixed text-xs">
        <thead>
          <tr class="border-b border-n-weak text-[10px] text-n-slate-11">
            <th class="px-2 py-2 text-left font-medium">
              {{ $t('CHATS_REPORTS.TIME_SUMMARY.METRIC') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.TIME_SUMMARY.AVERAGE') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.TIME_SUMMARY.MEDIAN') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.TIME_SUMMARY.MIN') }}
            </th>
            <th class="px-2 py-2 text-center font-medium">
              {{ $t('CHATS_REPORTS.TIME_SUMMARY.MAX') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            class="border-b border-n-weak/60 last:border-b-0"
          >
            <td class="px-2 py-2 text-left text-n-slate-12">{{ row.label }}</td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.average !== null && row.average !== undefined"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                :title="durationMetricTooltip(t, row.label, row.average)"
                @click="openValidCycles(row)"
              >
                {{ formatSeconds(row.average) }}
              </button>
              <span
                v-else
                class="tabular-nums"
                :title="durationMetricTooltip(t, row.label, row.average)"
              >
                {{ formatSeconds(row.average) }}
              </span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.median !== null && row.median !== undefined"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                :title="durationMetricTooltip(t, row.label, row.median)"
                @click="openValidCycles(row)"
              >
                {{ formatSeconds(row.median) }}
              </button>
              <span
                v-else
                class="tabular-nums"
                :title="durationMetricTooltip(t, row.label, row.median)"
              >
                {{ formatSeconds(row.median) }}
              </span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.minConversationDisplayId"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                :title="durationMetricTooltip(t, row.label, row.min)"
                @click="openConversation(row.minConversationDisplayId)"
              >
                {{ formatSeconds(row.min) }}
              </button>
              <span
                v-else
                class="tabular-nums"
                :title="durationMetricTooltip(t, row.label, row.min)"
              >
                {{ formatSeconds(row.min) }}
              </span>
            </td>
            <td class="px-2 py-2 text-center">
              <button
                v-if="row.maxConversationDisplayId"
                type="button"
                class="tabular-nums text-n-brand hover:underline"
                :title="durationMetricTooltip(t, row.label, row.max)"
                @click="openConversation(row.maxConversationDisplayId)"
              >
                {{ formatSeconds(row.max) }}
              </button>
              <span
                v-else
                class="tabular-nums"
                :title="durationMetricTooltip(t, row.label, row.max)"
              >
                {{ formatSeconds(row.max) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
